import {
  Color,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  RepeatWrapping,
  SphereBufferGeometry,
  Vector3,
  WebGLCubeRenderTarget,
} from 'three';

import GlobeMaterial from './GlobeMaterial';
import PinMaterial from './PinMaterial';
import GlobalImpactMaterial from './GlobalShockMaterial';
import LatLong from '../general/LatLong';
import LabelManager from '../ui/LabelManager';

/**
 * Globe is the 3D object wrapping the globe and its visual effects.
 */
export default class Globe extends Object3D {
  constructor(renderer, assetLibrary, radius) {
    super();
    this._assetLibrary = assetLibrary;
    this.randomLocations = false;
    this.globeRadius = radius || 1;
    this.shockSpeed = 0.005;
    this._createGlobeMesh(renderer, assetLibrary);

    this._groupPins = this._createPinMesh(assetLibrary, true);
    this._groupPins.material.pinToSphere = 1.0;
    this._groupPins.material.pinSize = 0.007;

    // stored by id -> mesh
    this._oldPins = new Map();
    this._detailedPins = new Map();
    this._pinMeshPool = [];

    // shock build dir tells the globe to change colour
    // basically, positive is from normal to shock, negative is from shock to normal
    // It's separate from the shockwave effect as it's the lasting effect on the globe.
    this._shockBuildDir = 0.0;

    this.labelManager = new LabelManager(this);
  }

  get pinPhase() {
    return this._groupPins.material.appearPhase;
  }

  set pinPhase(value) {
    this._groupPins.material.appearPhase = value;
  }

  setDetailPins(groupIDs, data) {
    const old = this._oldPins;
    const curr = this._detailedPins;

    // add the unused currents to the old
    curr.forEach((value, key) => old.set(key, value));

    // start a new collection to hold the new ones
    this._detailedPins = new Map();

    groupIDs = groupIDs || [];
    for (const id of groupIDs) {
      let pinMesh;
      // re-animate old pins back in if need be
      if (old.has(id)) {
        pinMesh = old.get(id);
        // no longer an "old" one
        old.delete(id);
      } else {
        // this one's group most likely changed, so we have to update data
        pinMesh = this._getPooledPinMesh();
        this._updatePins(data[id].pins, pinMesh);
      }

      this._detailedPins.set(id, pinMesh);
    }

    this._groupPins.material.setExcludedIDs(groupIDs);
  }

  setGroupPins(pins) {
    this._updatePins(pins, this._groupPins);
  }

  setLabels(canvases, pins) {
    this.labelManager.updateLabels(canvases, pins);
  }

  showLabels() {
    this.labelManager.visible = true;
  }

  hideLabels() {
    this.labelManager.visible = false;
  }

  _updatePins(pins, target) {
    const numPins = pins.length;
    const positions = new Float32Array(numPins * 3);
    const colors = new Float32Array(numPins * 3);
    const indices = new Float32Array(numPins);
    const values = new Float32Array(numPins);
    const color = new Color();
    const v = new Vector3();

    for (let i = 0; i < numPins; ++i) {
      const pin = pins[i];
      const i3 = i * 3;

      color.setHex(parseInt(pin.color));
      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;

      if (this.randomLocations) {
        LatLong.toVector3(
          MathUtils.randFloat(-90, 90),
          MathUtils.randFloat(-180, 180),
          this.globeRadius,
          v
        );
      } else {
        LatLong.toVector3(pin.location.lat, pin.location.long, this.globeRadius, v);
      }

      values[i] = pin.companyValue;
      indices[i] = i;

      positions[i3] = v.x;
      positions[i3 + 1] = v.y;
      positions[i3 + 2] = v.z;
    }

    const { geometry } = target;

    geometry.setAttribute('pinPosition', new InstancedBufferAttribute(positions, 3));
    geometry.setAttribute('pinColor', new InstancedBufferAttribute(colors, 3));
    geometry.setAttribute('pinValue', new InstancedBufferAttribute(values, 1));
    geometry.setAttribute('pinIndex', new InstancedBufferAttribute(indices, 1));
    geometry.maxInstancedCount = numPins;

    target.material.numPins = numPins;
  }

  update(dt) {
    const groupPinMaterial = this._groupPins.material;
    groupPinMaterial.phase += 0.2 * dt;

    this.globeMaterial.time += 0.03;

    if (this.globalShock) this.shockMaterial.phase += this.shockSpeed;

    if (this._shockBuildDir !== 0.0) {
      this.globeMaterial.shockPhase += this._shockBuildDir * this.shockSpeed;
    }

    this._detailedPins.forEach(pin => {
      const { material } = pin;
      material.phase = groupPinMaterial.phase;
      material.appearPhase = Math.min(material.appearPhase + 2.0 * dt, 1.0);
    });

    this._oldPins.forEach((pin, id) => {
      const { material } = pin;
      material.phase = groupPinMaterial.phase;
      material.appearPhase -= 2.0 * dt;

      if (material.appearPhase < 0.0) {
        material.appearPhase = 0.0;
        this._releasePooledPinMesh(pin);
        this._oldPins.delete(id);
      }
    });
  }

  get labelSize() {
    return this.labelManager.size;
  }

  set labelSize(value) {
    this.labelManager.size = value;
  }

  _createPinMesh(assetLibrary, groupPins) {
    const model = assetLibrary.get('pin/model');

    const srcGeometry = model.scene.children[0].geometry;

    const pinGeometry = new InstancedBufferGeometry();

    for (const attrib in srcGeometry.attributes) {
      pinGeometry.setAttribute(attrib, srcGeometry.attributes[attrib]);
    }

    pinGeometry.setIndex(srcGeometry.index);
    pinGeometry.computeVertexNormals();

    const pinMaterial = new PinMaterial(true, groupPins);
    pinMaterial.rippleMap = assetLibrary.get('pin/ripple');

    const mesh = new Mesh(pinGeometry, pinMaterial);
    mesh.matrixAutoUpdate = false;
    this.add(mesh);
    return mesh;
  }

  _createGlobeMesh(renderer, assetLibrary) {
    const geometry = new SphereBufferGeometry(this.globeRadius, 36, 24);
    const globeMaterial = new GlobeMaterial();
    const skybox = new WebGLCubeRenderTarget(256);
    skybox.fromEquirectangularTexture(renderer, assetLibrary.get('skybox'));

    const irradiance = new WebGLCubeRenderTarget(32);
    irradiance.fromEquirectangularTexture(renderer, assetLibrary.get('irradiance'));

    globeMaterial.envMap = skybox.texture;

    globeMaterial.irradianceMap = irradiance.texture;
    globeMaterial.sdf = assetLibrary.get('globe/sdf');

    globeMaterial.emissivePattern = assetLibrary.get('globe/lightsPattern');
    globeMaterial.emissivePattern.wrapS = RepeatWrapping;
    globeMaterial.emissivePattern.wrapT = RepeatWrapping;
    globeMaterial.emissiveMap = assetLibrary.get('globe/emission');
    globeMaterial.normalMap = assetLibrary.get('globe/normals');
    globeMaterial.normalDetailMap = assetLibrary.get('globe/normalDetailMap');
    globeMaterial.normalDetailMap.wrapS = RepeatWrapping;
    globeMaterial.normalDetailMap.wrapT = RepeatWrapping;
    globeMaterial.noiseMap = assetLibrary.get('globe/noise');
    globeMaterial.noiseMap.wrapS = RepeatWrapping;
    globeMaterial.noiseMap.wrapT = RepeatWrapping;

    this.globeMaterial = globeMaterial;
    this.globeMesh = new Mesh(geometry, globeMaterial);
    this.add(this.globeMesh);

    // only render when we're rendering 2.5D UI
    const occluderMaterial = new MeshBasicMaterial({ colorWrite: false });
    this.globeMeshOccluder = new Mesh(geometry, occluderMaterial);
    this.globeMeshOccluder.layers.set(2);
    this.add(this.globeMeshOccluder);

    const shockMaterial = new GlobalImpactMaterial();
    shockMaterial.radius = this.globeRadius;
    shockMaterial.noiseMap = assetLibrary.get('globe/noise');
    shockMaterial.normalMap = assetLibrary.get('globe/normals');
    shockMaterial.sdf = assetLibrary.get('globe/sdf');
    this.shockMaterial = shockMaterial;
    this.globalShockMesh = new Mesh(geometry, shockMaterial);
    this.globalShockMesh.scale.set(1.005, 1.005, 1.005);
  }

  showGlobalShock() {
    if (this.globalShock) return;
    this.shockMaterial.phase = 0;
    this.globalShock = true;
    this._shockBuildDir = 1;
    this.add(this.globalShockMesh);
  }

  hideGlobalShock() {
    this.globalShock = false;
    this.remove(this.globalShockMesh);
  }

  removeLastingShockEffect() {
    this._shockBuildDir = -1;
  }

  highlightPin(index, groupID) {
    this._detailedPins.forEach((mesh, id) => {
      // clear any other groups
      mesh.material.highlightPinID = id === groupID ? index : -1;
    });
  }

  // this tries to keep the used meshes to the minimum as is ever shown simultaneously
  _getPooledPinMesh() {
    let pinMesh;
    if (this._pinMeshPool.length > 0) {
      pinMesh = this._pinMeshPool.pop();
    } else {
      pinMesh = this._createPinMesh(this._assetLibrary);
    }

    const { material } = pinMesh;
    material.pinToSphere = 0.0;
    material.appearPhase = 0.0;
    pinMesh.visible = true;
    return pinMesh;
  }

  _releasePooledPinMesh(pinMesh) {
    pinMesh.visible = false;
    this._pinMeshPool.push(pinMesh);
  }
}
